import React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Alert from "@mui/material/Alert";
import Link from "@mui/material/Link";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import Switch from "@mui/material/Switch";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";
import { appThemeRadius, appThemeShadow } from "styles/appThemeTokens";

const uiFont = appThemeFonts.family.ui;

export const FormDropdownChevronIcon = styled(KeyboardArrowDownIcon)({
  width: "1em",
  height: "1em",
  fontSize: 16,
  flexShrink: 0,
});

/** Layout / modal shell — declared early so circular imports (e.g. GenericForm → FieldTypes → Tag UI)
 *  cannot read these bindings before initialization. */
export const ModalOverlay = styled(Box)(() => ({
  position: "fixed",
  top: "69px",
  left: "60px",
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0,0,0,0.5)",
  zIndex: 999,
}));

export const FlexContainer = styled(Box)(() => ({
  display: "flex",
  flex: "1",
}));

export const FlexOne = styled(Box)(() => ({
  flex: 1,
}));

export const ScrollContainer = styled(Box)(() => ({
  flexGrow: 1,
  maxHeight: "calc(100vh - 120px)",
  overflow: "scroll",
  paddingBottom: "100px",
  paddingRight: "20px",
  paddingTop: "20px",
}));

export const FooterContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  borderTop: "1px solid #e0e0e0",
  position: "fixed",
  bottom: 8,
  width: "90%",
  backgroundColor: theme.palette.background.paper,
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(1.25),
  zIndex: 9999,
}));

export const SecondaryButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  color: "#666",
  border: "1px solid #ccc",
  padding: "8px 10%",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "14px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
  },
}));

export const PrimaryButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "isDisabled",
})(({ theme, isDisabled }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  border: "none",
  padding: "8px 10%",
  borderRadius: "4px",
  cursor: "pointer",
  fontSize: "14px",
  textTransform: "none",
  opacity: isDisabled ? 0.6 : 1,
  pointerEvents: isDisabled ? "none" : "auto",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

export const FormContainer = styled(Paper, {
  shouldForwardProp: (prop) => prop !== "showBorder",
})(({ theme, showBorder }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  ...(showBorder && {
    border: `1px solid ${theme.palette.grey[300]}`,
  }),
  ...(!showBorder && {
    boxShadow: "none",
    border: "none",
  }),
}));

export const FieldWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "compact" && prop !== "inline",
})(({ theme, compact, inline }) => ({
  marginBottom: inline ? 0 : compact ? theme.spacing(1) : theme.spacing(3),
}));

export const GenericFormRoot = styled(Box)({
  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
    borderWidth: "2px !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "2px !important",
  },
});

export const FieldLabelContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  marginBottom: theme.spacing(0.5),
}));

export const FieldLabel = styled(Typography)(({ theme }) => ({
  variant: "body2",
  color: theme.palette.text.secondary,
}));

export const CollapsibleHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  cursor: "pointer",
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(1),
  "&:hover": {
    backgroundColor: theme.palette.grey[50],
  },
}));

export const CollapsibleHeaderContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const CollapsibleTitle = styled(Typography)(({ theme }) => ({
  variant: "subtitle1",
  fontWeight: 600,
}));

export const CountBadge = styled(Typography)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  paddingTop: theme.spacing(0.25),
  paddingBottom: theme.spacing(0.25),
  borderRadius: theme.spacing(1),
  fontWeight: 600,
  fontSize: "0.75rem",
  lineHeight: 1.5,
}));

export const CollapsibleContent = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(1),
}));

export const AddButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "disabled",
})(({ theme, disabled }) => ({
  color: disabled ? theme.palette.grey[400] : theme.palette.primary.main,
  textTransform: "none",
  paddingLeft: 0,
  justifyContent: "flex-start",
  "&:hover": {
    backgroundColor: "transparent",
    textDecoration: "underline",
  },
}));

export const ExpandIconButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
}));

export const DeleteIconButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.grey[500],
  height: 38,
  "&:hover": {
    color: theme.palette.error.main,
    backgroundColor: theme.palette.error.light + "10",
  },
}));

export const FilterButton = styled(Button)(({ theme }) => ({
  height: 38,
  whiteSpace: "nowrap",
  minWidth: "auto",
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
}));

export const ChipContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  flexWrap: "wrap",
  gap: theme.spacing(0.5),
}));

export const TagCardRow = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

export const TagAutocompleteWrapper = styled(Box)(() => ({
  flex: 1,
}));

export const ErrorText = styled(FormHelperText)(({ theme }) => ({
  marginLeft: 0,
  marginRight: 0,
  marginTop: theme.spacing(0.5),
}));

export const AlarmTagCardErrorHelperText = styled(FormHelperText)(({ theme }) => ({
  marginLeft: 0,
  marginTop: theme.spacing(0.5),
}));

export const TagArrayFieldErrorHelperText = styled(FormHelperText)(({ theme }) => ({
  marginLeft: 0,
  marginTop: theme.spacing(1),
}));

export const UnknownFieldTypeContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.error.light + "20",
  borderRadius: theme.spacing(1),
  border: `1px solid ${theme.palette.error.main}`,
}));

export const UnknownFieldTypeText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: 500,
}));

export const CustomFieldContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.spacing(1),
}));

export const CustomFieldTitle = styled(Typography)(({ theme }) => ({
  variant: "subtitle2",
  marginBottom: theme.spacing(1),
  fontWeight: 600,
}));

export const CustomFieldActions = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
}));

export const Divider = styled(Box)(({ theme }) => ({
  height: 1,
  backgroundColor: theme.palette.grey[300],
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

export const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(0),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),  
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: theme.palette.background.paper,
}));

export const FormSectionTitle = styled(Typography)(({ theme }) => ({
  variant: "h6",
  fontWeight: 600,
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(1.5),
  color: theme.palette.text.primary,
}));

export const InlineFlex = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

export const SpaceBetween = styled(Box)(() => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
}));

export const WidgetFormScrollContainer = styled(Box)(() => ({
  overflow: "scroll",
  paddingRight: "20px",
}));

export const WidgetFormContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "compact",
})(({ compact }) => ({
  paddingBottom: compact ? "20px" : "200px",
}));

export const FixedFooter = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  position: "fixed",
  bottom: 8,
  width: "90%",
  backgroundColor: theme.palette.background.paper,
  paddingTop: theme.spacing(2.5),
  paddingBottom: theme.spacing(1.25),
  zIndex: 9999,
}));

export const PrimaryActionButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  border: "none",
  padding: "8px 10%",
  borderRadius: "4px",
  fontSize: "14px",
  textTransform: "none",
  "&:hover": {
    backgroundColor: theme.palette.primary.dark,
  },
}));

export const WidgetTypeSelectorContainer = styled(Box)(({ theme }) => ({
  paddingBottom: theme.spacing(2),
  paddingTop: theme.spacing(0),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(2),
  border: `1px solid ${theme.palette.grey[300]}`,
  backgroundColor: theme.palette.background.paper,
}));

export const WidgetTypeSelectorTitle = styled(Typography)(({ theme }) => ({
  fontSize: "16px",
  lineHeight: "24px",
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  color: "#344767",
}));

export const WidgetTypeGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: theme.spacing(2),
  flexWrap: "wrap",
}));

export const WidgetTypeCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isSelected",
})(({ theme, isSelected }) => ({
  cursor: "pointer",
  border: isSelected
    ? "2px solid rgba(26, 107, 232, 0.5)"
    : "2px solid transparent",
  borderRadius: "8px",
  boxSizing: "border-box",
  backgroundColor: "#F2F2F0",
  padding: theme.spacing(1.25),
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  minWidth: "74px",
  minHeight: "66px",
  boxShadow:
    "0 1px 8px 0 rgba(0, 0, 0, 0.12), 0 3px 4px 0 rgba(0, 0, 0, 0.14), 0 3px 3px -2px rgba(0, 0, 0, 0.20)",
  transition: "transform 0.2s, border-color 0.2s",
  "&:hover": {
    transform: "scale(1.05)",
  },
  // Responsive styles
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
    minWidth: "62px",
    minHeight: "56px",
  },
}));

export const WidgetTypeIcon = styled("div")(() => ({
  width: "100%",
  height: "100%",
  maxWidth: "40px",
  maxHeight: "30px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  "& > *": {
    width: "100%",
    height: "100%",
    color: "#ff00ff",
  },
}));

// Error Boundary styled components
export const ErrorBoundaryContainer = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

export const ErrorBoundaryContent = styled(Box)(() => ({
  flex: 1,
}));

export const ErrorBoundaryTitle = styled(Typography)(({ theme }) => ({
  variant: "body2",
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}));

export const ErrorBoundaryMessage = styled(Typography)(({ theme }) => ({
  variant: "caption",
  component: "pre",
  color: theme.palette.error.dark,
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  fontFamily: "'IBM Plex Sans', sans-serif",
  fontSize: "0.75rem",
  margin: 0,
}));

export const RefreshIconButton = styled(IconButton)(({ theme }) => ({
  color: "inherit",
  "&:hover": {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
}));

export const InterpretationContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "compact",
})(({ theme, compact }) => ({
  marginBottom: compact ? 0 : theme.spacing(3),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const InterpretationText = styled(Typography)(({ theme }) => ({
  variant: "body2",
  color: theme.palette.text.secondary,
}));

export const SwapButton = styled(Link)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  cursor: "pointer",
  fontWeight: 600,
  "&:hover": {
    textDecoration: "underline",
  },
}));

export const CheckboxFieldContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));

export const CheckboxFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  marginRight: 0,
  "& .MuiFormControlLabel-label": {
    fontSize: "16px",
    color: "#333",
  },
}));

export const CheckboxFormControlLabelSized = styled(CheckboxFormControlLabel, {
  shouldForwardProp: (prop) => !["labelFontSize", "authConsent"].includes(prop),
})(({ labelFontSize, authConsent }) => {
  if (authConsent) {
    return {
      alignItems: "flex-start",
      margin: 0,
      width: "100%",
      gap: "10px",
      "& .MuiCheckbox-root": {
        padding: "2px 0 0",
        marginRight: 0,
      },
      "& .MuiFormControlLabel-label": {
        fontFamily: uiFont,
        fontSize: appThemeFonts.size.mdPlus,
        fontWeight: appThemeFonts.weight.regular,
        color: appThemeColors.text.muted,
        lineHeight: 1.55,
        cursor: "pointer",
        marginLeft: 0,
        paddingLeft: 0,
      },
    };
  }

  if (labelFontSize) {
    return {
      "& .MuiFormControlLabel-label": {
        fontSize:
          typeof labelFontSize === "number"
            ? `${labelFontSize}px`
            : labelFontSize,
      },
    };
  }

  return {};
});

export const MessageText = styled(Typography)(() => ({
  fontSize: "16px",
  color: "#333",
  lineHeight: "24px",
}));

export const FormErrorAlert = styled(Alert)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  "& .MuiAlert-message": {
    width: "100%",
  },
}));

export const TagFilterContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "showBorder",
})(({ theme, showBorder }) => ({
  ...(showBorder && {
    border: "1px solid",
    borderColor: theme.palette.divider,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  }),
}));

/** Collapsible region / site / area hierarchy panel inside tag filter UI */
export const TagHierarchyFiltersPanel = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.grey[50],
}));

export const TagHierarchyFiltersFieldsStack = styled(Stack)(({ theme }) => ({
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

export const TagHierarchyFiltersActionsStack = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "flex-end",
  gap: theme.spacing(1),
}));

export const TagCardWrapper = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

export const TagCardOrderHeader = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  marginBottom: theme.spacing(1),
}));

export const TagCardOrderButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  padding: theme.spacing(0.25),
  "& .MuiSvgIcon-root": {
    fontSize: 18,
  },
}));

export const InfoIcon = styled(InfoOutlinedIcon)(({ theme }) => ({
  fontSize: 18,
  color: theme.palette.grey[500],
  marginLeft: theme.spacing(0.5),
  cursor: "help",
}));

const StyledFormTextField = styled(TextField)(() => ({
  fontFamily: uiFont,
  "& .MuiInputLabel-root": {
    fontFamily: uiFont,
    fontSize: "13.5px",
    color: appThemeColors.text.primary,
    "&.Mui-focused": {
      color: appThemeColors.text.lightBlue,
      fontWeight: 600,
    },
    "&.MuiInputLabel-shrink": {
      color: appThemeColors.text.primary,
      fontWeight: 600,
      "&.Mui-focused": {
        color: appThemeColors.text.lightBlue,
      },
    },
    "&.Mui-error": {
      color: appThemeColors.semantic.danger,
      "&.Mui-focused": {
        color: appThemeColors.semantic.danger,
      },
      "&.MuiInputLabel-shrink": {
        color: appThemeColors.semantic.danger,
        "&.Mui-focused": {
          color: appThemeColors.semantic.danger,
        },
      },
    },
    "&.Mui-disabled": {
      color: appThemeColors.text.faint,
    },
  },
  "& .MuiInputLabel-outlined.MuiInputLabel-sizeSmall": {
    transform: "translate(14px, 10px) scale(1)",
    "&.MuiInputLabel-shrink": {
      transform: "translate(14px, -7px) scale(0.75)",
    },
  },
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    borderRadius: 9,
    fontSize: "13.5px",
    fontFamily: uiFont,
    "& fieldset": {
      borderColor: appThemeColors.border.strong,
      borderWidth: 1,
    },
    "&:hover fieldset": {
      borderColor: appThemeColors.border.strong,
    },
    "&.Mui-focused": {
      "& fieldset": {
        borderColor: appThemeColors.text.lightBlue,
        borderWidth: "2px !important",
      },
    },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      borderColor: `${appThemeColors.semantic.danger} !important`,
    },
    "&.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${appThemeColors.semantic.danger} !important`,
    },
    "&.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${appThemeColors.semantic.danger} !important`,
      borderWidth: "2px !important",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "0 12px",
    height: 38,
    boxSizing: "border-box",
    fontSize: "13.5px",
    fontFamily: uiFont,
    color: appThemeColors.text.primary,
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: appThemeColors.text.faint,
    opacity: 1,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    minHeight: 38,
    boxSizing: "border-box",
    padding: "0 12px !important",
    fontSize: "13.5px",
    fontFamily: uiFont,
  },
  "& .MuiSelect-icon": {
    fontSize: 16,
  },
  "& .MuiFormHelperText-root": {
    fontFamily: uiFont,
    fontSize: "11.5px",
    marginLeft: 0,
    color: appThemeColors.text.faint,
  },
  "& .MuiFormHelperText-root.Mui-error": {
    color: appThemeColors.semantic.danger,
  },
}));

export const formInlinePlaceholderSx = (theme, { error = false } = {}) => ({
  color: error ? appThemeColors.semantic.danger : theme.palette.text.secondary,
});

export const FORM_OUTLINED_HEIGHT = 38;
export const COMPACT_INLINE_OUTLINED_HEIGHT = 32;

export const compactInlineSelectSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: COMPACT_INLINE_OUTLINED_HEIGHT,
    height: COMPACT_INLINE_OUTLINED_HEIGHT,
    boxSizing: "border-box",
    borderRadius: appThemeRadius.md,
    fontSize: "12.5px",
    fontFamily: uiFont,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiSelect-icon": {
    fontSize: 16,
  },
};

/** Outlined Select/MultiSelect — matches TextField error border when FormControl error alone is insufficient. */
export const formSelectErrorSx = () => ({
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": {
    borderColor: `${appThemeColors.semantic.danger} !important`,
  },
  "& .MuiOutlinedInput-root.Mui-error:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: `${appThemeColors.semantic.danger} !important`,
  },
  "& .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline":
    {
      borderColor: `${appThemeColors.semantic.danger} !important`,
      borderWidth: 2,
    },
  "& .MuiInputLabel-root.Mui-error": {
    color: `${appThemeColors.semantic.danger} !important`,
  },
  "& .MuiInputLabel-root.Mui-error.Mui-focused": {
    color: `${appThemeColors.semantic.danger} !important`,
  },
});

export const GenericFormFieldLabelRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(0.75),
}));

export const GenericFormFieldLabelText = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  color: theme.palette.text.primary,
}));

export const GenericFormFieldHintText = styled(Typography)(({ theme }) => ({
  display: "block",
  marginTop: theme.spacing(0.75),
  lineHeight: 1.35,
  color: theme.palette.text.secondary,
}));

export const FieldBlock = styled(Box)({
  display: "flex",
  flexDirection: "column",
});

export const FieldHint = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "$error",
})(({ $error }) => ({
  display: "block",
  fontSize: $error ? appThemeFonts.size.md : appThemeFonts.size.smPlus,
  fontWeight: 400,
  color: $error ? appThemeColors.semantic.danger : appThemeColors.text.faint,
  marginTop: 6,
  lineHeight: 1.4,
}));

export const GenericFormFieldHint = FieldHint;

export const PasswordFieldInputAdornment = styled(InputAdornment)(({ theme }) => ({
  gap: theme.spacing(0.25),
  flexShrink: 0,
}));

const multiSelectHiddenLegendSx = {
  "& .MuiOutlinedInput-notchedOutline legend": {
    display: "none",
    maxWidth: 0.01,
    padding: 0,
  },
};

const formOutlinedSelectSx = {
  "& .MuiOutlinedInput-root": {
    minHeight: FORM_OUTLINED_HEIGHT,
    height: FORM_OUTLINED_HEIGHT,
    boxSizing: "border-box",
    borderRadius: 9,
    fontSize: "13.5px",
    fontFamily: uiFont,
  },
  "& .MuiSelect-select": {
    display: "flex",
    alignItems: "center",
    minHeight: FORM_OUTLINED_HEIGHT,
    boxSizing: "border-box",
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: "13.5px",
    fontFamily: uiFont,
  },
  "& .MuiSelect-icon": {
    fontSize: 16,
  },
};

export const MultiSelectFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => !["compactInline", "hideLegend"].includes(prop),
})(({ theme, compactInline, hideLegend, error }) => ({
  ...(compactInline ? compactInlineSelectSx : formOutlinedSelectSx),
  ...(error ? formSelectErrorSx() : {}),
  ...(hideLegend ? multiSelectHiddenLegendSx : {}),
}));

export const MultiSelectInlinePlaceholder = styled("span", {
  shouldForwardProp: (prop) => prop !== "hasError",
})(({ theme, hasError }) => ({
  ...formInlinePlaceholderSx(theme, { error: hasError }),
}));

export const MultiSelectCommaValue = styled(Box)({
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

/** react-phone-input-2 wrapper for GenericForm PhoneField — matches FormTextField outlined styling. */
export const GenericFormPhoneInputRoot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hasError",
})(({ hasError }) => {
  const defaultBorderColor = appThemeColors.border.strong;
  const interactiveBorderColor = hasError
    ? appThemeColors.semantic.danger
    : appThemeColors.text.lightBlue;
  const borderColor = hasError ? appThemeColors.semantic.danger : defaultBorderColor;
  const outlinedHeight = 38;
  const outlinedRadius = 9;

  return {
    width: "100%",
    overflow: "visible",
    position: "relative",
    "& .react-tel-input": {
      width: "100%",
      height: outlinedHeight,
      fontFamily: uiFont,
      position: "relative",
      boxSizing: "border-box",
      border: `1px solid ${borderColor}`,
      borderRadius: `${outlinedRadius}px`,
      overflow: "visible",
      backgroundColor: "#FFFFFF",
    },
    "& .react-tel-input .flag-dropdown": {
      position: "absolute",
      top: 0,
      bottom: 0,
      backgroundColor: "transparent !important",
      border: "none !important",
      borderRadius: "0 !important",
    },
    "& .react-tel-input .flag-dropdown.open": {
      zIndex: 2,
    },
    "& .react-tel-input .flag-dropdown:hover, & .react-tel-input .flag-dropdown.open": {
      backgroundColor: "transparent !important",
    },
    "& .react-tel-input .country-list": {
      position: "absolute",
      top: "100%",
      left: 0,
      zIndex: 1300,
      width: 300,
      maxHeight: 220,
      marginTop: 4,
      padding: 0,
      listStyle: "none",
      borderRadius: outlinedRadius,
      border: `1px solid ${appThemeColors.border.default}`,
      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
      backgroundColor: "#FFFFFF",
      overflowY: "auto",
      fontFamily: uiFont,
      fontSize: "13.5px",
    },
    "& .react-tel-input .country-list .country": {
      padding: "8px 12px",
    },
    "& .react-tel-input .country-list .country.highlight": {
      backgroundColor: appThemeColors.background.selected,
    },
    "& .react-tel-input .selected-flag": {
      width: 46,
      paddingLeft: 12,
      backgroundColor: "transparent !important",
      borderRadius: "0 !important",
    },
    "& .react-tel-input .selected-flag:hover, & .react-tel-input .selected-flag:focus": {
      backgroundColor: "transparent !important",
    },
    "& .react-tel-input .form-control": {
      width: "100%",
      height: "100% !important",
      minHeight: "unset !important",
      margin: "0 !important",
      paddingLeft: 48,
      paddingRight: 12,
      fontSize: "13.5px",
      fontFamily: uiFont,
      color: appThemeColors.text.primary,
      lineHeight: "normal",
      boxSizing: "border-box",
      backgroundColor: "transparent !important",
      border: "none !important",
      borderRadius: "0 !important",
      boxShadow: "none !important",
      outline: "none",
    },
    "& .react-tel-input .form-control::placeholder": {
      color: appThemeColors.text.faint,
      opacity: 1,
    },
    "& .react-tel-input .form-control.invalid-number": {
      border: "none !important",
      backgroundColor: "transparent !important",
    },
    "&:focus-within .react-tel-input": {
      borderColor: interactiveBorderColor,
      boxShadow: "none",
    },
  };
});

export const compactInlineAutocompleteSx = {
  width: "100%",
  ...compactInlineSelectSx,
  "& .MuiOutlinedInput-root": {
    minHeight: `${COMPACT_INLINE_OUTLINED_HEIGHT}px !important`,
    height: COMPACT_INLINE_OUTLINED_HEIGHT,
    maxHeight: COMPACT_INLINE_OUTLINED_HEIGHT,
    boxSizing: "border-box",
    borderRadius: `${appThemeRadius.md} !important`,
    fontSize: "12.5px",
    fontFamily: uiFont,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "9px",
    paddingRight: "28px !important",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
    minHeight: `${COMPACT_INLINE_OUTLINED_HEIGHT}px !important`,
    height: COMPACT_INLINE_OUTLINED_HEIGHT,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "9px",
    paddingRight: "28px !important",
  },
  "& .MuiOutlinedInput-input": {
    height: "auto !important",
    minHeight: 0,
    padding: "0 !important",
    fontSize: "12.5px",
    fontFamily: uiFont,
    boxSizing: "border-box",
  },
  "& .MuiAutocomplete-input": {
    padding: "0 !important",
    minWidth: 0,
    fontSize: "12.5px",
    fontFamily: uiFont,
    height: "auto !important",
  },
  "& .MuiAutocomplete-endAdornment": {
    right: 4,
  },
  "& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root": {
    fontSize: 16,
  },
  "& .MuiFormHelperText-root": {
    fontSize: "11px",
    marginTop: "4px",
    marginLeft: 0,
  },
  "& .MuiOutlinedInput-root.Mui-focused fieldset": {
    borderWidth: "2px !important",
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderWidth: "2px !important",
  },
};

export const formAutocompleteInputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    minHeight: FORM_OUTLINED_HEIGHT,
    height: FORM_OUTLINED_HEIGHT,
    boxSizing: "border-box",
  },
  "& .MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
    minHeight: FORM_OUTLINED_HEIGHT,
    height: FORM_OUTLINED_HEIGHT,
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: "14px",
    paddingRight: "39px !important",
    boxSizing: "border-box",
  },
  "& .MuiAutocomplete-input.MuiInputBase-inputSizeSmall": {
    padding: "8.5px 0 !important",
    minWidth: 0,
  },
  "& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root": {
    fontSize: 16,
  },
};
export const formAutocompletePaperSx = {
  fontFamily: uiFont,
  fontSize: "13.5px",
  backgroundColor: appThemeColors.background.paper,
  border: `1px solid ${appThemeColors.border.default}`,
  borderRadius: appThemeRadius.lg,
  boxShadow: appThemeShadow.menu,
};

export const MlTagSelectAutocompleteTextField = styled(TextField)(({ theme }) => ({
  width: "100%",
  "& .MuiOutlinedInput-root": {
    minHeight: FORM_OUTLINED_HEIGHT,
    height: FORM_OUTLINED_HEIGHT,
    boxSizing: "border-box",
    borderRadius: 9,
    fontSize: "13.5px",
    fontFamily: uiFont,
  },
  "& .MuiOutlinedInput-input": {
    padding: "0 12px",
    height: FORM_OUTLINED_HEIGHT,
    boxSizing: "border-box",
  },
  "& .MuiAutocomplete-popupIndicator .MuiSvgIcon-root": {
    fontSize: 16,
  },
  "& .MuiFormHelperText-root": {
    marginLeft: 0,
    marginRight: 0,
    marginTop: theme.spacing(0.5),
    paddingLeft: 0,
    paddingRight: 0,
    textAlign: "left",
  },
}));

/** Outlined/small form inputs; browser autocomplete disabled by default (override with `autoComplete`). */
export const FormTextField = React.forwardRef(function FormTextField(
  { autoComplete = "off", ...props },
  ref
) {
  return (
    <StyledFormTextField ref={ref} autoComplete={autoComplete} {...props} />
  );
});

export const StatusInterpretationContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

export const StatusInterpretationLabel = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
}));

export const StatusValueBox = styled(Box)(({ theme }) => ({
  border: "1px solid",
  borderColor: theme.palette.divider,
  borderRadius: theme.spacing(1),
  paddingTop: theme.spacing(2),
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  height: "100%",
}));

export const StatusHeaderBox = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: theme.spacing(2),
}));

export const StatusChipDot = styled(Box)(() => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  backgroundColor: "#fff",
  marginLeft: 8,
}));

export const ColorPreviewBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== "color",
})(({ theme, color }) => ({
  width: 20,
  height: 20,
  borderRadius: "4px",
  backgroundColor: color,
  border: "1px solid",
  borderColor: theme.palette.divider,
}));

export const ColorOptionBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
}));

export const StatusFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  alignItems: "flex-start",
  marginBottom: theme.spacing(2),
}));

export const StatusPreviewChip = styled(Chip, {
  shouldForwardProp: (prop) => prop !== "bgcolor",
})(({ bgcolor }) => ({
  backgroundColor: bgcolor,
  color: "#fff",
  fontWeight: "medium",
}));

export const StatusFieldStack = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

export const StatusInfoIcon = styled(InfoOutlinedIcon)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.text.secondary,
}));

export const CustomizedSwitch = styled(Switch)(({ theme }) => ({
  width: 28,
  height: 16,
  padding: 0,
  display: "flex",
  flexDirection: "row",
  flexShrink: 0,
  "&:active": {
    "& .MuiSwitch-thumb": {
      width: 15,
    },
    "& .MuiSwitch-switchBase.Mui-checked": {
      transform: "translateX(9px)",
    },
  },
  "& .MuiSwitch-switchBase": {
    padding: 2,
    "&.Mui-checked": {
      transform: "translateX(12px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        opacity: 1,
        backgroundColor: appThemeColors.text.lightBlue,
        ...theme.applyStyles("dark", {
          backgroundColor: appThemeColors.text.lightBlue,
        }),
      },
    },
  },
  "& .MuiSwitch-thumb": {
    boxShadow: "0 2px 4px 0 rgb(0 35 11 / 20%)",
    width: 12,
    height: 12,
    borderRadius: 6,
    transition: theme.transitions.create(["width"], {
      duration: 200,
    }),
  },
  "& .MuiSwitch-track": {
    borderRadius: 16 / 2,
    opacity: 1,
    backgroundColor: appThemeColors.border.dark,
    boxSizing: "border-box",
    ...theme.applyStyles("dark", {
      backgroundColor: appThemeColors.border.dark,
    }),
  },
  "& .MuiSwitch-switchBase + .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: appThemeColors.border.dark,
  },
  "& .MuiSwitch-switchBase.Mui-disabled:not(.Mui-checked) + .MuiSwitch-track": {
    opacity: 1,
    backgroundColor: `${appThemeColors.border.dark} !important`,
  },
  "& .MuiSwitch-switchBase.Mui-disabled.Mui-checked": {
    cursor: "not-allowed",
    color: "#fff",
    "& + .MuiSwitch-track": {
      opacity: 1,
      backgroundColor: `${appThemeColors.text.lightBlue} !important`,
    },
  },
  "&.Mui-disabled": {
    cursor: "not-allowed",
  },
}));

